import {ReactElement, useEffect, lazy, Suspense} from "react";
import {createBrowserRouter, Navigate, RouterProvider} from "react-router-dom";
import * as Sentry from "@sentry/react";
import DevourProviders from "@/DevourProviders";
import PageWrapper from "./components/routing/PageWrapper";
import { applyDOMErrorPrevention, preventGoogleTranslate, preventGoogleMapsErrors } from "@/utils/domErrorPrevention";
import {DEVOUR_RESTAURANT_ID} from "./constants/restaurantConstants";
import LoginPage from "./pages/LoginPage";
import "react-loading-skeleton/dist/skeleton.css";
import {isDev} from "@/utils/isDev";
import useOverwolfInterop from "./hooks/useOverwolfInterop";
import InitialRedirect from "@/components/InitialRedirect";
import Play from "./pages/Play";
import PlayCockpit from "./pages/PlayCockpit";
import PlayCockpitDemo from "./pages/PlayCockpitDemo";
import PlayCockpitV2 from "./pages/PlayCockpitV2";
import PlayCockpitV2Test from "./pages/PlayCockpitV2Test";
import CockpitV3 from "./pages/play/CockpitV3";
import CockpitVariants from "./pages/CockpitVariants";
import CockpitExperiments from "./pages/CockpitExperiments";
import GridCommandEnhanced from "./pages/GridCommandEnhanced";
import GridCommandDepth from "./pages/GridCommandDepth";
import GridCommandAlive from "./pages/GridCommandAlive";
import GridCommandPlus from "./pages/GridCommandPlus";
import GridCommandStructured from "./pages/GridCommandStructured";
import GridCommandV3 from "./pages/GridCommandV3";
import GridCommandV4 from "./pages/GridCommandV4";
import GridCommandWithData from "./pages/GridCommandWithData";
import NeuralCockpit from "./pages/NeuralCockpit";
import GamersCockpit from "./pages/GamersCockpit";
import CyberneticCommand from "./pages/CyberneticCommand";
import BrandscapeHub from "./pages/BrandscapeHub";
import EpicGamingHub from "./pages/EpicGamingHub";
import ProGamingDashboard from "./pages/ProGamingDashboard";
import VisualFirstDashboard from "./pages/VisualFirstDashboard";
import TheNexus from "./pages/TheNexus";
import TheArena from "./pages/TheArena";
import PlayEnhanced from "./pages/PlayEnhanced";
import DevourPlayDiscoverPage from "./pages/devourplay/DevourPlayDiscoverPage";

// LAZY LOADING - Phase 3: Policy pages
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));

// LAZY LOADING - Phase 4: Low-traffic pages
const ComponentDemoPage = lazy(() => import("./pages/ComponentDemoPage"));
const WithdrawPage = lazy(() => import("./pages/WithdrawPage"));
const ErrorPage = lazy(() => import("./pages/ErrorPage"));
const MobileAffiliatesPage = lazy(() => import("./pages/MobileAffiliatesPage"));

// LAZY LOADING - Phase 5: GoFrens pages (deprecated/unused)
const GoFrensPage = lazy(() => import("./pages/GoFrensPage"));
const GoFrensMyNfts = lazy(() => import("./pages/gofrens/GoFrensMyNfts"));
const GoFrensSearchResultsPage = lazy(() => import("./pages/gofrens/GoFrensSearchResultsPage"));

// LAZY LOADING - Phase 6: Commerce and gaming pages
const BuyDPay = lazy(() => import("./pages/BuyDPay"));
const LootBoxPage = lazy(() => import("./pages/LootBoxPage").then(module => ({ default: module.LootBoxPage })));
const CollectiblesPage = lazy(() => import("./pages/CollectiblesPage").then(module => ({ default: module.CollectiblesPage })));
const TheIndustryPassPage = lazy(() => import("./pages/TheIndustryPassPage"));

// LAZY LOADING - Phase 7: User account pages
const RestaurantFavoritesPage = lazy(() => import("./pages/restaurants/RestaurantFavoritesPage"));
const MenuOrderListPage = lazy(() => import("./pages/menu-orders/MenuOrderListPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const MobileReferAFriendPage = lazy(() => import("./pages/MobileReferAFriendPage"));
const CommunityPoolPage = lazy(() => import("./pages/CommunityPoolPage"));

// LAZY LOADING - Phase 8: Marketing/external pages
const DevourPlayLandingPage = lazy(() => import("./pages/DevourPlayLandingPage"));

// LAZY LOADING - Phase 9: Gaming/social pages (higher risk)
const GoVip = lazy(() => import("./pages/GoVip"));
const OverwolfGatherPage = lazy(() => import("./pages/overwolf/OverwolfGatherPage"));

// LAZY LOADING - Phase 10: Restaurant pages (critical)
const RestaurantPage = lazy(() => import("./pages/restaurants/RestaurantPage"));
const RestaurantSearchResultsPage = lazy(() => import("./pages/restaurants/RestaurantSearchResultsPage"));
const RestaurantMapLandingPage = lazy(() => import("./pages/brandMap/RestaurantMapLandingPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const MenuOrderFailPage = lazy(() => import("./pages/menu-orders/MenuOrderFailPage"));
const RestaurantRedirectHandler = lazy(() => import("./components/routing/RestaurantRedirectHandler"));

// LAZY LOADING - Phase 11: Landing/marketing pages
const Landing = lazy(() => import("./pages/Landing"));
const MenuOrderSuccessSwitcher = lazy(() => import("./components/menuOrder/MenuOrderSuccessSwitcher"));

// LAZY LOADING - Phase 12: Overwolf pages
const OverwolfLandingPage = lazy(() => import("./pages/overwolf/OverwolfLandingPage"));
const OverwolfDiscoverPage = lazy(() => import("./pages/overwolf/OverwolfDiscoverPage"));
const OverwolfGoVip = lazy(() => import("./pages/overwolf/OverwolfGoVip"));

// Simple loading fallback - minimal to avoid scroll issues
const LazyLoadingFallback = () => <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>;
const BrandChallengePage = lazy(() => import("./pages/BrandChallengePage"));
const GamePage = lazy(() => import("./pages/games/GamePage"));

function App(): ReactElement {

    const {setupOverwolf} = useOverwolfInterop();

    useEffect(() => {
        /*
         * Apply DOM error prevention patches to handle Google Translate and browser extensions
         * This prevents "Failed to execute 'removeChild' on 'Node'" errors site-wide
         */
        applyDOMErrorPrevention();
        preventGoogleTranslate();

        /*
         * Prevent Google Maps API errors on pages that don't need Maps
         * This prevents "undefined is not an object (evaluating 'new za.jD')" errors
         */
        preventGoogleMapsErrors();

        // Handle chunk loading errors on iOS
        window.addEventListener("error", (e) => {
            if (e.message?.includes("Loading chunk") || e.message?.includes("text/html")) {
                e.preventDefault();
                window.location.reload();
            }
        });

        setupOverwolf();
    }, []);

    const errorElement =
				<PageWrapper
				    page={
				        <Suspense fallback={<LazyLoadingFallback />}>
				            <ErrorPage/>
				        </Suspense>
				    }
				    sidebarDockingEnabled={true}
				    authRequired={false}
				/>;

    return (
        <DevourProviders>

            <RouterProvider
                router={Sentry.wrapCreateBrowserRouter(createBrowserRouter)([
                    isDev() && {
                        path: "/demo",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <ComponentDemoPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														/>,
                    },
                    {
                        path: "/",
                        element:
														<InitialRedirect checkOverwolf={true}/>,
                    },
                    {
                        path: "/home",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <Landing/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    authRequired={false}
														    showMobileStickyFooter={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/devourplay-discover",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <DevourPlayDiscoverPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														    controlMobileStickyFooterWithRedux={true}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/overwolf-landing",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <OverwolfLandingPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/overwolf-discover",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <OverwolfDiscoverPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/overwolf-gather",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <OverwolfGatherPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/overwolf-go-vip",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <OverwolfGoVip/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/play",
                        element:
														<PageWrapper
														    page={<CockpitV3/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/play-cockpit",
                        element:
														<PageWrapper
														    page={<PlayCockpit/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/play-cockpit-demo",
                        element:
														<PageWrapper
														    page={<PlayCockpitDemo/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/play-cockpit-v2",
                        element:
														<PageWrapper
														    page={<PlayCockpitV2/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={true}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/play-cockpit-v2-test",
                        element:
														<PageWrapper
														    page={<PlayCockpitV2Test/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/cockpit-v3",
                        element:
														<PageWrapper
														    page={<CockpitV3/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/cockpit-variants",
                        element:
														<PageWrapper
														    page={<CockpitVariants/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/cockpit-experiments",
                        element:
														<PageWrapper
														    page={<CockpitExperiments/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/grid-command",
                        element:
														<PageWrapper
														    page={<GridCommandEnhanced/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/grid-command-depth",
                        element:
														<PageWrapper
														    page={<GridCommandDepth/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/grid-command-alive",
                        element:
														<PageWrapper
														    page={<GridCommandAlive/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/grid-command-plus",
                        element:
														<PageWrapper
														    page={<GridCommandPlus/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/grid-command-structured",
                        element:
														<PageWrapper
														    page={<GridCommandStructured/>}
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    forceDarkTheme={true}
														/>,
                    },
                    {
                        path: "/grid-command-v3",
                        element:
										<PageWrapper
										    page={<GridCommandV3/>}
										    sidebarDockingEnabled={false}
										    showFooter={false}
										    showMobileStickyFooter={false}
										    authRequired={false}
										    forceDarkTheme={true}
										/>,
                    },
                    {
                        path: "/grid-command-v4",
                        element:
										<PageWrapper
										    page={<GridCommandV4/>}
										    sidebarDockingEnabled={false}
										    showFooter={false}
										    showMobileStickyFooter={false}
										    authRequired={false}
										    forceDarkTheme={true}
										/>,
                    },
                    {
                        path: "/grid-command-data",
                        element:
												<PageWrapper
												    page={<GridCommandWithData/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/neural-cockpit",
                        element:
												<PageWrapper
												    page={<NeuralCockpit/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/gamers-cockpit",
                        element:
												<PageWrapper
												    page={<GamersCockpit/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/cybernetic-command",
                        element:
												<PageWrapper
												    page={<CyberneticCommand/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/brandscape-hub",
                        element:
												<PageWrapper
												    page={<BrandscapeHub/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/epic-gaming-hub",
                        element:
												<PageWrapper
												    page={<EpicGamingHub/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/pro-gaming-dashboard",
                        element:
												<PageWrapper
												    page={<ProGamingDashboard/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/visual-first-dashboard",
                        element:
												<PageWrapper
												    page={<VisualFirstDashboard/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/the-nexus",
                        element:
												<PageWrapper
												    page={<TheNexus/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/the-arena",
                        element:
												<PageWrapper
												    page={<TheArena/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/play-enhanced",
                        element:
												<PageWrapper
												    page={<PlayEnhanced/>}
												    sidebarDockingEnabled={false}
												    showFooter={false}
												    showMobileStickyFooter={false}
												    authRequired={false}
												    forceDarkTheme={true}
												/>,
                    },
                    {
                        path: "/log-in",
                        element:
														<PageWrapper
														    page={<LoginPage/>}
														    sidebarDockingEnabled={true}
														/>,
                    },
                    {
                        path: "/account",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <AccountPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/community-pool",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <CommunityPoolPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														/>,
                    },
                    {
                        path: "/dpay-load",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <AccountPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    /*
                     * Food ordering deactivated - keeping for potential future use
                     * {
                     *     path: "/favorites",
                     *     element:
                     * 	<PageWrapper
                     * 	    page={
                     * 	        <Suspense fallback={<LazyLoadingFallback />}>
                     * 	            <RestaurantFavoritesPage/>
                     * 	        </Suspense>
                     * 	    }
                     * 	    sidebarDockingEnabled={true}
                     * 	    authRequired={true}
                     * 	/>
                     *     ,
                     * },
                     */
                    {
                        path: "/refer-a-friend",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <MobileReferAFriendPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/affiliates",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <MobileAffiliatesPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/gofrens",
                        element:
														<Navigate
														    to="/gofriends"
														    replace={true}
														/>,
                    },
                    {
                        path: "/gofriends",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <GoFrensPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														/>,
                    },
                    {
                        path: "/guilds",
                        element: <Navigate to="/play?view=guilds" replace />,
                    },
                    {
                        path: "/the-industry-pass/",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <TheIndustryPassPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														/>,
                    },
                    {
                        path: "/devourlootbox/:nftPurchaseId",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <LootBoxPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														/>,

                    },
                    {
                        path: "/devourlootbox",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <LootBoxPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														    showFooter={true}
														/>,
                    },
                    /*
                     *{
                     *  path: "/gofriends-rewards",
                     *  element:
                     *      <PageWrapper
                     *          page={<GoFrensRewardsPage/>}
                     *          sidebarDockingEnabled={true}
                     *          authRequired={true}
                     *      />
                     *  ,
                     *},
                     */
                    {
                        path: "/gofriends-my-nfts",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <GoFrensMyNfts/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/gofriends/search-results",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <GoFrensSearchResultsPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,

                    },
                    {
                        path: "/go-vip-dashboard",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <GoVip/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														/>,
                    },
                    {
                        path: "/go-vip",
                        element:
														<Navigate
														    to={"/go-vip-dashboard"}
														    replace={true}
														/>,
                    },
                    {
                        path: "/withdraw",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <WithdrawPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/buy-dpay",
                        element:
														<Navigate
														    to="/load-fuel"
														    replace={true}
														/>,
                    },
                    {
                        path: "/load-fuel",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <BuyDPay/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														/>,
                    },
                    {
                        path: "/privacy-policy",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <PrivacyPolicyPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														/>,
                    },
                    {
                        path: "/terms-of-use",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <TermsOfUsePage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														/>,
                    },
                    {
                        path: "/devourplay",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <DevourPlayLandingPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showFooter={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														/>,
                    },
                    {
                        path: "/cookie-policy",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <CookiePolicyPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														/>,
                    },
                    {
                        path: "/brands/:slug",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <RestaurantMapLandingPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showMobileStickyFooter={false}
														    authRequired={false}
														    pagePaddingAdjusterClassName="restaurant-map-landing-page-padding-adjuster"
														    showFooter={false}
														    themeExcluded={true}
														    hideLoader={true}
														/>,
                    },
                    {
                        path: "/games/:slug",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <GamePage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    showMobileStickyFooter={true}
														    authRequired={false}
														    showFooter={false}
														/>,
                    },
                    {
                        path: "/lootshop",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <RestaurantPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														    controlMobileStickyFooterWithRedux={true}
														/>,
                    },
                    /*
                     * Food ordering deactivated - restaurant browsing disabled
                     * {
                     *     path: "/restaurants",
                     *     element:
                     * 	<InitialRedirect checkOverwolf={false}/>
                     *     ,
                     * },
                     * Food ordering deactivated - search functionality disabled
                     * {
                     *     path: "/restaurants/search/:placeId",
                     *     element:
                     * 	<PageWrapper
                     * 	    page={
                     * 	        <Suspense fallback={<LazyLoadingFallback />}>
                     * 	            <RestaurantSearchResultsPage/>
                     * 	        </Suspense>
                     * 	    }
                     * 	    sidebarDockingEnabled={true}
                     * 	    authRequired={false}
                     * 	/>
                     *     ,
                     * },
                     * {
                     *     path: "/restaurants/search",
                     *     element:
                     * 	<Navigate
                     * 	    to="/"
                     * 	    replace={true}
                     * 	/>
                     *     ,
                     * },
                     */
                    [
                        "/restaurants/:state/:cityName/:address/:restaurantName/:restaurantId",
                        "/restaurants/:state/:cityName/:address/:restaurantName/:restaurantId/:placeId",
                        "/restaurants/:restaurantId",
                        "/restaurants/:restaurantId/:placeId",
                    ].map(p => ({
                        path: p,
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <RestaurantRedirectHandler/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														    controlMobileStickyFooterWithRedux={true}
														/>,
                    })),
                    {
                        path: "/checkout/:menuOrderId",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <CheckoutPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={false}
														    showMobileStickyFooter={false}
														    authRequired={true}
														    showFooter={false}
														/>,
                    },
                    {
                        path: "/loot-shop",
                        element:
														<Navigate
														    to={`/restaurants/${DEVOUR_RESTAURANT_ID}`}
														    replace={true}
														/>,
                    },
                    {
                        path: "/lootshop",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <RestaurantPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														/>,
                    },
                    {
                        path: "/collectibles",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <CollectiblesPage/>
														        </Suspense>
														    }
														    showFooter={false}
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/menu-orders/",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <MenuOrderListPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/menu-orders/:menuOrderId/success",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <MenuOrderSuccessSwitcher/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/menu-orders/:menuOrderId/fail",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <MenuOrderFailPage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={true}
														/>,
                    },
                    {
                        path: "/challenges/:id",
                        element:
														<PageWrapper
														    page={
														        <Suspense fallback={<LazyLoadingFallback />}>
														            <BrandChallengePage/>
														        </Suspense>
														    }
														    sidebarDockingEnabled={true}
														    authRequired={false}
														    forceDarkTheme={true}
														    showFooter={false}
														/>,
                    },
                    {
                        path: "/*",
                        element:
														<Navigate
														    to={"/"}
														    replace={true}
														/>,
                    },
                ].flat().map((routeItem) => {
                    // Prepend errorElement to route item
                    return {
                        errorElement,
                        ...routeItem,
                    };
                }))}
            />

        </DevourProviders>
    );
}

export default App;
